import React, { useState, useEffect } from "react";
import LocationHeader from "../components/locationManagement/LocationHeader";
import LocationTable from "../components/locationManagement/LocationTable";
import AddLocationModal from "../components/locationManagement/AddLocationModal";
import { useDeleteLocation, Location } from "../hooks/useLocation";
import { Alert } from "antd";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useNavigate } from "react-router-dom";

const LocationManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null
  );
  const [filters, setFilters] = useState<Record<string, any>>({});

  // Get user permissions
  const { hasPermission } = usePermissions();
  const canViewLocations = hasPermission(PERMISSIONS.VIEW_LOCATIONS);
  const canAddLocations = hasPermission(PERMISSIONS.ADD_LOCATIONS);
  const canModifyLocations = hasPermission(PERMISSIONS.MODIFY_LOCATIONS);
  const canDeleteLocations = hasPermission(PERMISSIONS.DELETE_LOCATIONS);

  // Redirect if user doesn't have permission to view locations
  useEffect(() => {
    if (!canViewLocations) {
      // Redirect to unauthorized page with a delay to prevent render issues
      const timeout = setTimeout(() => {
        navigate("/unauthorized");
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [canViewLocations, navigate]);

  // Handle Opening Modal for Adding Location
  const handleAddLocation = () => {
    if (!canAddLocations) {
      return; // Don't open modal if user doesn't have permission
    }
    setSelectedLocation(null);
    setModalVisible(true);
  };

  // Handle Editing Location
  const handleModifyLocation = (location: Location) => {
    if (!canModifyLocations) {
      return; // Don't open modal if user doesn't have permission
    }
    setSelectedLocation(location);
    setModalVisible(true);
  };

  // Handle Modal Submit Success
  const handleModalSubmit = () => {
    setModalVisible(false);
    setSelectedLocation(null);
  };

  // Handle Modal Cancel
  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedLocation(null);
  };

  // Handle Filters
  const handleApplyFilters = (newFilters: Record<string, any>) => {
    console.log("Applying location filters:", newFilters);
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    console.log("Clearing location filters");
    setFilters({});
  };

  // Handle Deleting Location
  const { mutate: deleteLocation } = useDeleteLocation();
  const handleDeleteLocation = (locationId: number) => {
    if (!canDeleteLocations) {
      return; // Don't delete if user doesn't have permission
    }
    deleteLocation(locationId);
  };

  // If user doesn't have permission to view locations, show access denied message
  if (!canViewLocations) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to access location management. Please contact your administrator if you need access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      <LocationHeader
        onAddLocation={handleAddLocation}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        canAddLocations={canAddLocations}
      />
      <LocationTable
        onModifyLocation={handleModifyLocation}
        onDeleteLocation={handleDeleteLocation}
        filters={filters}
        canModifyLocations={canModifyLocations}
        canDeleteLocations={canDeleteLocations}
      />

      {/* Add or Modify Location Modal */}
      <AddLocationModal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
        location={selectedLocation}
      />
    </div>
  );
};

export default LocationManagementPage;
